/**
 * Live Preview Panel — typeset mini-resume for the Edit Resume lightbox.
 *
 * Reads the CURRENT in-editor resumejson draft (no network calls, nothing
 * saved): name + contact line, then every non-omitted section in the current
 * section order using the current custom names. The section that scrollspy says
 * is in view gets a sky wash so the rail, the editor and the paper agree.
 *
 * The page-fit number is an ESTIMATE — bullets are measured with the same
 * PDF line-break model the editor uses (`splitBulletIntoLines`), everything
 * else uses a coarse characters-per-line model.
 */
import { Eye, X } from 'lucide-react';
import { splitBulletIntoLines } from '../utils/pdfLineBreaks';
import type { GenerationType, ResumeJSON, SubcomponentType } from '../types';

// Coarse line model for the non-bullet content (chars that fit one printed
// line at each type size) plus how many printed lines fit on a page.
/**
 * Help/Docs anchor root. The panel only exists inside an OPEN resume editor and
 * is revealed by the top bar's Preview toggle, so its keys hang off the
 * editor's path — see `help/highlightTargets.ts` for the catalog entries.
 */
const HELP = 'mainapp.resumes.resumeN.preview';

const CHARS_PER_LINE_BODY = 110;
const CHARS_PER_LINE_LIST = 95;
const LINES_PER_PAGE = 52;
const SECTION_HEADER_LINES = 1.4;

const included = <T extends { include?: string }>(items: T[] | undefined): T[] =>
  (items || []).filter(i => i?.include !== '0');

function wrapped(text: string, per: number): number {
  const t = (text || '').trim();
  if (!t) return 0;
  return Math.max(1, Math.ceil(t.length / per));
}

interface ResumePreviewPanelProps {
  resume: ResumeJSON;
  /** Reorderable sections in print order (contact renders as the header line). */
  orderedSections: SubcomponentType[];
  getLabel: (component: SubcomponentType) => string;
  genTypes: Record<SubcomponentType, GenerationType>;
  /** Nav key of the block currently in view (scrollspy). */
  activeKey: string | null;
  onClose: () => void;
}

export function ResumePreviewPanel({
  resume, orderedSections, getLabel, genTypes, activeKey, onClose,
}: ResumePreviewPanelProps) {
  const visible = orderedSections.filter(s => genTypes[s] !== 'omit');
  const contacts = genTypes.contact === 'omit' ? [] : included(resume.contact);

  // ---- content + line estimate (single pass so they can't disagree) -------
  let lines = 2 + (contacts.length ? 1 : 0);   // name block + contact line

  const blocks: { key: SubcomponentType; label: string; body: React.ReactNode }[] = [];

  for (const component of visible) {
    lines += SECTION_HEADER_LINES;
    let body: React.ReactNode = null;

    if (component === 'summary') {
      const text = (resume.summary || '').trim();
      lines += wrapped(text, CHARS_PER_LINE_BODY);
      body = text
        ? <p className="text-[6.5px] leading-[1.65] text-gray-700">{text}</p>
        : <p className="text-[6.5px] italic text-gray-400">Written per job from your experience.</p>;

    } else if (component === 'highlights') {
      const items = (resume.highlights || []).filter(Boolean);
      if (items.length) {
        items.forEach(h => { lines += wrapped(h, CHARS_PER_LINE_LIST); });
        body = <ul className="space-y-0.5">{items.map((h, i) => <PreviewBullet key={i} text={h} />)}</ul>;
      } else {
        lines += 3;
        body = <p className="text-[6.5px] italic text-gray-400">Selected per job from your strongest matching bullets.</p>;
      }

    } else if (component === 'skills') {
      const skills = included(resume.skills);
      const groups = resume.skill_groups || [];
      if (groups.length) {
        const rows = groups
          .map(g => ({ title: g.title, names: skills.filter(s => s.group_id === g.id).map(s => s.title) }))
          .filter(r => r.names.length);
        const ungrouped = skills.filter(s => !s.group_id || !groups.some(g => g.id === s.group_id)).map(s => s.title);
        if (ungrouped.length) rows.push({ title: 'Other', names: ungrouped });
        rows.forEach(r => { lines += wrapped(`${r.title}: ${r.names.join(', ')}`, CHARS_PER_LINE_LIST); });
        body = (
          <div className="space-y-0.5">
            {rows.map(r => (
              <p key={r.title} className="text-[6.3px] leading-[1.6] text-gray-700">
                <span className="font-semibold text-primary-700">{r.title}: </span>{r.names.join(', ')}
              </p>
            ))}
          </div>
        );
      } else {
        const names = skills.map(s => s.title);
        lines += wrapped(names.join(', '), CHARS_PER_LINE_LIST);
        body = <p className="text-[6.3px] leading-[1.6] text-gray-700">{names.join(', ')}</p>;
      }

    } else if (component === 'experience') {
      const companies = included(resume.experience);
      body = (
        <div className="space-y-1.5">
          {companies.map((c, ci) => {
            lines += 1;
            const roles = included(c.roles);
            return (
              <div key={ci}>
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-[6.8px] font-bold text-gray-800 truncate">{c.company}</span>
                  <span className="text-[6.1px] text-gray-500 whitespace-nowrap">
                    {c.startDate}{(c.current || c.endDate) ? ` – ${c.current ? 'Present' : c.endDate}` : ''}
                  </span>
                </div>
                {roles.map((r, ri) => {
                  lines += 1;
                  const bullets = included(r.bullets);
                  bullets.forEach(b => { lines += splitBulletIntoLines(b.text || '').length; });
                  return (
                    <div key={ri} className="mt-0.5">
                      <div className="text-[6.4px] italic text-gray-600">{r.title}</div>
                      <ul className="mt-0.5 space-y-0.5">
                        {bullets.map((b, bi) => <PreviewBullet key={bi} text={b.text} />)}
                      </ul>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      );

    } else {
      // education / awards / presentations / publications / portfolios
      const items = included((resume as unknown as Record<string, { title?: string; org?: string; issuer?: string; event?: string; year?: string; include?: string }[]>)[component]);
      lines += items.length;
      body = (
        <div className="space-y-0.5">
          {items.map((it, i) => (
            <div key={i} className="flex items-baseline justify-between gap-2">
              <span className="text-[6.4px] text-gray-700 truncate">
                {it.title}
                {(it.org || it.issuer || it.event) && <span className="text-gray-500"> — {it.org || it.issuer || it.event}</span>}
              </span>
              {it.year && <span className="text-[6.1px] text-gray-500 whitespace-nowrap">{it.year}</span>}
            </div>
          ))}
        </div>
      );
    }

    blocks.push({ key: component, label: getLabel(component), body });
  }

  const pages = Math.max(0.1, Math.round((lines / LINES_PER_PAGE) * 10) / 10);
  // Page-fit meter: the track spans whole pages (at least two), a solid segment
  // per full page and a lighter segment for the partly-filled last page.
  const capacity = Math.max(2, Math.ceil(pages));
  const fullPages = Math.floor(pages);
  const partial = Math.max(0, pages - fullPages);
  const fullPct = Math.min(100, (fullPages / capacity) * 100);
  const partPct = Math.min(100 - fullPct, (partial / capacity) * 100);
  const fitNote = partial > 0.04
    ? `page ${fullPages + 1} is ${Math.round(partial * 100)}% full`
    : `${fullPages} full page${fullPages === 1 ? '' : 's'}`;
  const activeLabel = blocks.find(b => b.key === activeKey)?.label
    ?? (activeKey === 'contact' ? getLabel('contact') : null);

  return (
    <div
      className="absolute inset-y-0 right-0 w-[392px] max-w-[62%] bg-white border-l border-gray-200 shadow-[-12px_0_28px_rgba(17,24,39,0.10)] flex flex-col z-20"
      data-help-target={`${HELP}.panel`}
      data-testid="resume-preview-panel"
    >
      <div className="flex items-center gap-2.5 px-3.5 py-2.5 border-b border-gray-200 flex-shrink-0">
        <Eye className="w-4 h-4 text-gray-500 flex-shrink-0" aria-hidden="true" />
        <span className="min-w-0 flex-1">
          <span className="block text-[13.5px] font-semibold text-gray-900 leading-tight">Live preview</span>
          <span className="block text-[11px] text-gray-500 truncate">Untailored base resume · section order applies</span>
        </span>
        {activeLabel && (
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-primary-50 text-primary-700 border border-primary-200 truncate max-w-[120px] flex-shrink-0">
            {activeLabel}
          </span>
        )}
        <button
          type="button"
          onClick={onClose}
          data-help-target={`${HELP}.close`}
          className="w-[30px] h-[30px] grid place-items-center flex-shrink-0 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
          aria-label="Hide live preview"
          title="Hide live preview"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto bg-gray-100 p-3">
        <div className="bg-white border border-gray-200 rounded-[3px] shadow-[0_1px_4px_rgba(17,24,39,0.09)] px-5 pt-4 pb-6">
          <div className="text-center">
            <div className="text-[12.5px] font-bold uppercase tracking-[0.09em] text-gray-900">
              {resume.name || 'Your Name'}
            </div>
            {contacts.length > 0 && (
              <div className="mt-1 text-[6.3px] text-gray-500">
                {contacts.map(c => c.title).filter(Boolean).join('  ·  ')}
              </div>
            )}
          </div>
          <div className="mt-2 mb-2 border-t border-gray-300" />

          {blocks.map(b => (
            <div
              key={b.key}
              className={`-mx-1.5 mb-1.5 px-1.5 py-1 rounded-sm border-l-2 transition-colors ${
                activeKey === b.key ? 'bg-primary-50 border-primary-500' : 'border-transparent'
              }`}
            >
              <div className="text-[6.6px] font-bold uppercase tracking-[0.13em] text-primary-700 mb-1">
                {b.label}
              </div>
              {b.body}
            </div>
          ))}

          {blocks.length === 0 && (
            <p className="text-[7px] italic text-gray-400 text-center py-4">Every section is set to Omit.</p>
          )}
        </div>
      </div>

      <div className="flex-shrink-0 border-t border-gray-200 px-3.5 py-2.5 bg-white" data-help-target={`${HELP}.pagefit`}>
        <div className="flex items-center justify-between gap-2 text-[11.5px] text-gray-700">
          <span>Page fit</span>
          <span className="tabular-nums">
            <b className="font-semibold">~{pages} page{pages === 1 ? '' : 's'}</b>
            <span className="text-gray-500"> · {fitNote}</span>
          </span>
        </div>
        <div className="mt-1.5 h-[7px] w-full bg-gray-100 rounded-full overflow-hidden flex gap-[2px]">
          <span className="h-full bg-primary-600" style={{ width: `${fullPct}%` }} />
          <span className="h-full bg-primary-400" style={{ width: `${partPct}%` }} />
        </div>
        <p className="mt-1.5 text-[11px] leading-snug text-gray-500">
          Estimated at full length. Per job, SkillSnap prunes the lowest-matching items until the resume fits.
        </p>
      </div>
    </div>
  );
}

function PreviewBullet({ text }: { text: string }) {
  return (
    <li className="flex gap-1 text-[6.3px] leading-[1.6] text-gray-700">
      <span className="mt-[3px] w-[2.5px] h-[2.5px] rounded-full bg-gray-400 flex-shrink-0" />
      <span className="min-w-0">{text}</span>
    </li>
  );
}
